import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '@infra/i18n';
import { cn } from '@shared/lib';
import {
  ANIMATION_DURATIONS,
  ANIMATION_DELAYS,
  EASING,
  STAGGER_DELAY_MULTIPLIER,
} from '@shared/constants';

/**
 * Props for PageWrapper component
 */
interface PageWrapperProps {
  /** Page content */
  children: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Maximum width constraint */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
}

const maxWidthClasses = {
  sm: 'max-w-screen-sm',
  md: 'max-w-screen-md',
  lg: 'max-w-screen-lg',
  xl: 'max-w-screen-xl',
  '2xl': 'max-w-screen-2xl',
  full: 'max-w-full',
};

/**
 * Page wrapper component with animation and max-width constraints
 *
 * Provides consistent page layout with fade-in animation and responsive max-width.
 *
 * @param props - PageWrapper component props
 * @returns Animated page wrapper div
 */
export function PageWrapper({ children, className, maxWidth = 'full' }: PageWrapperProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: ANIMATION_DURATIONS.NORMAL, ease: EASING.EASE_OUT_CUBIC }}
      className={cn('p-4 md:p-6 mx-auto', maxWidthClasses[maxWidth], className)}
    >
      {children}
    </motion.div>
  );
}

interface StaggerContainerProps {
  children: React.ReactNode;
  className?: string;
  staggerDelay?: number;
}

export function StaggerContainer({
  children,
  className,
  staggerDelay = STAGGER_DELAY_MULTIPLIER,
}: StaggerContainerProps) {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: staggerDelay,
            delayChildren: ANIMATION_DELAYS.SMALL,
          },
        },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 16, scale: 0.98 },
        visible: {
          opacity: 1,
          y: 0,
          scale: 1,
          transition: {
            duration: ANIMATION_DURATIONS.SLOW,
            ease: EASING.EASE_OUT_CUBIC,
          },
        },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

interface AnimatedCardProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  elevated?: boolean;
}

export function AnimatedCard({
  children,
  className,
  onClick,
  elevated = false,
}: AnimatedCardProps) {
  return (
    <motion.div
      whileHover={{
        y: -3,
        boxShadow: 'var(--shadow-elevation-3)',
        transition: { duration: ANIMATION_DURATIONS.FAST, ease: EASING.EASE_OUT_CUBIC },
      }}
      whileTap={onClick ? { scale: 0.99 } : undefined}
      transition={{ duration: ANIMATION_DURATIONS.FAST, ease: EASING.EASE_OUT_CUBIC }}
      onClick={onClick}
      className={cn(
        'glass-card rounded-xl border border-border/40 bg-card p-5 transition-shadow',
        elevated ? 'shadow-elevation-2' : 'shadow-elevation-1',
        onClick && 'cursor-pointer',
        className
      )}
    >
      {children}
    </motion.div>
  );
}

// Skeleton loading components
export const SkeletonCard = React.memo(function SkeletonCard({
  className,
}: {
  className?: string;
}) {
  return (
    <div className={cn('bg-card rounded-xl border border-border/50 p-5', className)}>
      <div className="skeleton-shimmer h-4 w-1/3 rounded-md mb-3" />
      <div className="skeleton-shimmer h-8 w-2/3 rounded-md mb-4" />
      <div className="skeleton-shimmer h-3 w-full rounded-md mb-2" />
      <div className="skeleton-shimmer h-3 w-4/5 rounded-md" />
    </div>
  );
});

export const SkeletonTable = React.memo(function SkeletonTable({ rows = 5 }: { rows?: number }) {
  return (
    <div className="bg-card rounded-xl border border-border/50 overflow-hidden">
      <div className="px-5 py-3 border-b border-border/50">
        <div className="skeleton-shimmer h-4 w-32 rounded-md" />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="px-5 py-4 border-b border-border/30 last:border-0">
          <div className="flex items-center gap-4">
            <div className="skeleton-shimmer w-10 h-10 rounded-full flex-shrink-0" />
            <div className="flex-1">
              <div className="skeleton-shimmer h-4 w-1/4 rounded-md mb-2" />
              <div className="skeleton-shimmer h-3 w-1/3 rounded-md" />
            </div>
            <div className="skeleton-shimmer h-6 w-16 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
});

export const SkeletonChart = React.memo(function SkeletonChart({
  className,
}: {
  className?: string;
}) {
  return (
    <div className={cn('bg-card rounded-xl border border-border/50 p-5', className)}>
      <div className="skeleton-shimmer h-4 w-1/4 rounded-md mb-4" />
      <div className="h-48 flex items-end gap-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            className="skeleton-shimmer flex-1 rounded-t-md"
            style={{ height: `${30 + Math.random() * 60}%` }}
          />
        ))}
      </div>
    </div>
  );
});

// Empty state component
interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
}

export const EmptyState = React.memo(function EmptyState({
  icon,
  title,
  description,
  action,
}: EmptyStateProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-12 px-4 text-center"
    >
      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground max-w-sm mb-4">{description}</p>
      {action}
    </motion.div>
  );
});

interface InlineErrorBannerProps {
  /** The reason as the server or the network gave it — never a generic string. */
  message: string;
  onRetry?: () => void;
}

/**
 * Non-blocking failure notice for a screen that still has something to show.
 *
 * `ErrorState` replaces the whole page, which is wrong when only one of several
 * parallel fetches failed — the rest of the dashboard is real and useful. This
 * sits above the content and names the actual cause, so "the request failed" is
 * never rendered as "you have no data".
 */
export const InlineErrorBanner = React.memo(function InlineErrorBanner({
  message,
  onRetry,
}: InlineErrorBannerProps) {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className="mb-4 flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3"
    >
      <svg
        className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      <p className="flex-1 text-sm text-foreground">
        {t('common:errors.loadFailed', { message })}
      </p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="flex-shrink-0 text-sm font-medium text-destructive underline-offset-2 hover:underline"
        >
          {t('common:buttons.retry')}
        </button>
      )}
    </div>
  );
});

// Error state component
interface ErrorStateProps {
  title?: string;
  message: string;
  onRetry?: () => void;
  actionLabel?: string;
}

export const ErrorState = React.memo(function ErrorState({
  title,
  message,
  onRetry,
  actionLabel,
}: ErrorStateProps) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-12 px-4 text-center"
    >
      <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
        <svg
          className="w-8 h-8 text-destructive"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">
        {title ?? t('common:messages.error')}
      </h3>
      <p className="text-sm text-muted-foreground max-w-sm mb-4">{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover-elevate"
        >
          {actionLabel ?? t('common:buttons.retry')}
        </button>
      )}
    </motion.div>
  );
});
